/** TanStack Query hooks for the AI chat service: create sessions and stream messages via NDJSON. */
import { useMutation } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { streamNdjson } from '@/shared/utils/streamNdjson';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Chat {
  id: string;
}

export type ChatEvent =
  | { type: 'token'; content: string }
  | { type: 'session_ready'; stepVersionIds: string[]; lessonIds: string[]; stepCount: number }
  | { type: 'tool_start'; name: string }
  | { type: 'tool_end'; name: string }
  /**
   * Fired by the backend after a tool round completes, before the next round's
   * tokens start streaming. The frontend should wipe any text it has
   * accumulated so far so the final (post-tool) reply replaces rather than
   * appends to any preamble the LLM emitted while calling tools.
   */
  | { type: 'reset_stream' }
  | { type: 'job_created'; jobId: string }
  | { type: 'suggestions'; items: string[]; placeholder: string | null }
  | { type: 'done' }
  | { type: 'error'; message: string };

// ─── API functions ────────────────────────────────────────────────────────────

async function createChat(): Promise<Chat> {
  return api.post<Chat>('/me/chat', {});
}

async function sendChatMessage(
  chatId: string,
  content: string,
  onEvent: (event: ChatEvent) => void
): Promise<void> {
  await streamNdjson<ChatEvent>(
    `/me/chat/${chatId}/messages`,
    { method: 'POST', body: JSON.stringify({ content }) },
    { onEvent }
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Create a new chat session. */
export function useCreateChat() {
  return useMutation({
    mutationFn: createChat,
  });
}

/** Export sendMessage for direct use in the chat state hook. */
export { sendChatMessage };
